import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { ConflictError } from '@ember-data/adapter/error';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { format, parseISO } from 'date-fns';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import PowerSelect from 'ember-power-select/components/power-select';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';
import type DocumentUploadPageAdapter from 'garaje/adapters/document-upload-page';
import CloseButton from 'garaje/components/close-button';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import type { RequiredDocumentSignature } from 'garaje/components/flows/sections/required-documents/list';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type { ReUploadConfig, ReuploadPolicy } from 'garaje/models/custom-user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';
import buildReUploadConfig from 'garaje/utils/build-reupload-config';

const CREATE_NEW_OPTION = { id: '__create_new__', title: 'Create new document type' } as const;
const SEPARATOR = { id: '__separator__', title: '', isSeparator: true } as const;
const MANAGE_TYPES_OPTION = {
  id: '__manage_types__',
  title: 'Manage document types',
  isManageOption: true,
} as const;
type TemplateOption =
  | CustomUserDocumentTemplateModel
  | typeof CREATE_NEW_OPTION
  | typeof SEPARATOR
  | typeof MANAGE_TYPES_OPTION;

interface ConfigurationPayloadData {
  id: string;
  type: string;
  attributes: {
    'allow-llm-parsing': boolean;
    're-upload-config': ReUploadConfig | undefined;
    active: boolean;
  };
  relationships: {
    'user-document-template': {
      data: { id: string; type: string };
    };
  };
}

interface FlowsSectionsRequiredDocumentsAddModalSignature {
  Args: {
    documentUploadPageId?: string;
    existingDocument?: RequiredDocumentSignature;
    existingDocumentIds?: string[];
    onClose: () => void;
    onOpenManage?: () => void;
    onSaved: (document: RequiredDocumentSignature) => void;
  };
}

export default class FlowsSectionsRequiredDocumentsAddModal extends Component<FlowsSectionsRequiredDocumentsAddModalSignature> {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;

  @tracked selectedOption: TemplateOption | null = null;
  @tracked newTitle = '';
  @tracked newDescription = '';
  @tracked parsingEnabled = false;
  @tracked reuploadPolicy: ReuploadPolicy = 'always';
  @tracked reuploadDays = '365';
  @tracked reuploadDate: Date | null = null;
  @tracked showDatePicker = false;
  @tracked templates: CustomUserDocumentTemplateModel[] = [];

  get isEditing(): boolean {
    return Boolean(this.args.existingDocument);
  }

  get isCreatingNew(): boolean {
    return this.selectedOption?.id === CREATE_NEW_OPTION.id;
  }

  get canSave(): boolean {
    if (!this.selectedOption) return false;
    if (this.reuploadPolicy === 'on_date_yearly' && !this.reuploadDate) return false;
    if (this.selectedOption.id === CREATE_NEW_OPTION.id) {
      return this.newTitle.trim().length > 0;
    }
    return true;
  }

  get dropdownOptions(): TemplateOption[] {
    const excludeIds = new Set(this.args.existingDocumentIds ?? []);
    const filtered = this.templates.filter((t) => !excludeIds.has(t.id));
    // TODO: Re-enable CREATE_NEW_OPTION when inline template creation from dropdown is needed
    // Only show separator when there are templates above it
    if (filtered.length > 0) {
      return [...filtered, SEPARATOR, MANAGE_TYPES_OPTION];
    }
    return [MANAGE_TYPES_OPTION];
  }

  constructor(owner: unknown, args: FlowsSectionsRequiredDocumentsAddModalSignature['Args']) {
    super(owner, args);
    void this.loadTemplates.perform();
  }

  loadTemplates = dropTask(async () => {
    if (!this.state.currentCompany?.id) return;
    try {
      const results = await this.store.query('custom-user-document-template', {});
      this.templates = results.slice();
      if (this.args.existingDocument) {
        const match = this.templates.find((t) => t.id === this.args.existingDocument!.id);
        if (match) {
          this.selectedOption = match;
          this.populateFromTemplate(match);
          this.parsingEnabled = this.args.existingDocument.allowLlmParsing ?? false;
          this.restorePolicyFromDocument(this.args.existingDocument);
        }
      }
    } catch (e) {
      this.logger.warn('[AddModal] Failed to load document templates:', e as Error);
    }
  });

  populateFromTemplate(template: CustomUserDocumentTemplateModel): void {
    this.newTitle = template.title;
    this.newDescription = template.description ?? '';
    this.parsingEnabled = template.allowLlmParsing;
    if (template.reUploadConfig) {
      this.restorePolicyFromConfig(template.reUploadConfig);
    }
  }

  getReUploadConfig(): ReUploadConfig {
    return buildReUploadConfig(this.reuploadPolicy, this.reuploadDays, this.reuploadDate);
  }

  restorePolicyFromConfig(config: ReUploadConfig): void {
    this.reuploadPolicy = config.policy;
    if (config.policy === 'after_days' && config.days) {
      this.reuploadDays = String(config.days);
    }
    if (config.policy === 'on_date_yearly' && config.month && config.day) {
      const now = new Date();
      this.reuploadDate = new Date(now.getFullYear(), config.month - 1, config.day);
    }
  }

  /**
   * Push a configuration response payload into the store and return the record.
   */
  private pushConfigurationResponse = (response: {
    data: ConfigurationPayloadData;
  }): UserDocumentTemplateConfigurationModel => {
    this.store.pushPayload({ data: response.data });
    return this.store.peekRecord(
      'user-document-template-configuration',
      response.data.id,
    ) as UserDocumentTemplateConfigurationModel;
  };

  saveDocument = dropTask(async () => {
    let template: CustomUserDocumentTemplateModel | null = null;

    try {
      const adapter = this.store.adapterFor('document-upload-page') as DocumentUploadPageAdapter;
      const configAttrs = {
        reUploadConfig: this.getReUploadConfig(),
        allowLlmParsing: this.parsingEnabled,
      };

      // TODO: Re-enable when inline template creation from dropdown is restored.
      // Creating company-level templates is now handled by the manage modal only.
      // if (this.isCreatingNew) {
      //   createdNewTemplate = true;
      //   template = this.store.createRecord('custom-user-document-template', { ... });
      //   await template.save();
      // }

      // Flow modal only selects existing templates — it never creates new ones.
      // It only updates the per-flow config (reUploadConfig, allowLlmParsing) below.
      template = this.selectedOption as CustomUserDocumentTemplateModel;
      // Ensure template has no dirty state that would trigger a PATCH to the
      // company-level endpoint when Ember Data reconciles after the per-flow save.
      // rollbackAttributes() is a no-op if nothing is dirty.
      template.rollbackAttributes();

      if (this.args.documentUploadPageId) {
        let configuration: UserDocumentTemplateConfigurationModel;
        try {
          const templateSwapped =
            this.isEditing && this.args.existingDocument && this.args.existingDocument.id !== template.id;

          if (this.isEditing && !templateSwapped) {
            // Update existing per-page configuration
            const response = (await adapter.updateTemplate(
              this.args.documentUploadPageId,
              template.id,
              configAttrs,
            )) as { data: ConfigurationPayloadData };
            configuration = this.pushConfigurationResponse(response);
          } else {
            // Add template with per-page configuration (new or swapped)
            // Add BEFORE removing old template so a failure doesn't leave the document unlinked
            try {
              const response = (await adapter.addTemplate(
                this.args.documentUploadPageId,
                template.id,
                configAttrs,
              )) as { data: ConfigurationPayloadData };
              configuration = this.pushConfigurationResponse(response);
            } catch (e: unknown) {
              // Handle 409 conflict — template already linked to this page
              if (e instanceof ConflictError) {
                this.flashMessages.showAndHideFlash('error', 'This document is already linked to this flow.');
                return;
              }
              throw e;
            }

            if (templateSwapped) {
              // Remove old template from the page after new one is successfully added
              await adapter.removeFromRelationship(
                'document-upload-page',
                this.args.documentUploadPageId,
                'custom-user-document-templates',
                [{ type: 'custom-user-document-templates', id: this.args.existingDocument!.id }],
              );
            }
          }
        } catch (e) {
          this.logger.error('[AddModal] Failed to update document-upload-page configuration', e as Error);
          this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
          return;
        }

        this.args.onSaved({
          id: template.id,
          title: template.title,
          subtitle: template.description,
          policyName: this.policyDisplayName,
          reuploadPolicy: this.reuploadPolicy,
          reuploadDays: this.reuploadDays,
          reuploadDate: this.reuploadDate?.toISOString(),
          position: this.args.existingDocument?.position ?? 0,
          configurationId: configuration!.id,
          allowLlmParsing: this.parsingEnabled,
        });
      } else {
        // No page ID — legacy path, just notify parent
        this.args.onSaved({
          id: template.id,
          title: template.title,
          subtitle: template.description,
          policyName: this.policyDisplayName,
          reuploadPolicy: this.reuploadPolicy,
          reuploadDays: this.reuploadDays,
          reuploadDate: this.reuploadDate?.toISOString(),
          position: this.args.existingDocument?.position ?? 0,
        });
      }

      this.flashMessages.showAndHideFlash('success', this.isEditing ? 'Document updated.' : 'Document added.');
      this.args.onClose();
    } catch (e) {
      this.logger.error('[AddModal] saveDocument failed', e as Error);
      if (template) {
        template.rollbackAttributes();
      }
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    }
  });

  selectOption = (option: TemplateOption): void => {
    if ('isSeparator' in option) return;
    if ('isManageOption' in option) {
      this.args.onClose();
      this.args.onOpenManage?.();
      return;
    }
    this.selectedOption = option;
    // Reset to defaults first, then let populateFromTemplate override with template config
    this.reuploadPolicy = 'always';
    this.reuploadDays = '365';
    this.reuploadDate = null;
    if (option.id === CREATE_NEW_OPTION.id) {
      this.newTitle = '';
      this.newDescription = '';
      this.parsingEnabled = false;
    } else {
      const tmpl = option as CustomUserDocumentTemplateModel;
      // Ensure template has no dirty state from manage modal edits in the same session
      // — prevents Ember Data from auto-PATCHing the company template when we save per-flow config
      tmpl.rollbackAttributes();
      this.populateFromTemplate(tmpl);
    }
  };

  updateTitle = (value: string): void => {
    this.newTitle = value;
  };

  updateDescription = (value: string): void => {
    this.newDescription = value;
  };

  updateParsingEnabled = (checked: boolean): void => {
    this.parsingEnabled = checked;
  };

  updateReuploadPolicy = (evt: Event): void => {
    this.reuploadPolicy = (evt.target as HTMLInputElement).value as ReuploadPolicy;
  };

  updateReuploadDays = (value: string): void => {
    this.reuploadDays = value.replace(/\D/g, '');
  };

  get reuploadDateDisplay(): string {
    if (!this.reuploadDate) return 'Select a date';
    return format(this.reuploadDate, 'MMM d, yyyy');
  }

  get policyDisplayName(): string {
    switch (this.reuploadPolicy) {
      case 'always':
        return 'Always';
      case 'never':
        return 'Never';
      case 'after_days':
        return `After ${this.reuploadDays}d`;
      case 'on_expiration':
        return 'On expiration';
      case 'on_date_yearly':
        return this.reuploadDate ? format(this.reuploadDate, 'MMM d') : 'Yearly date';
      default:
        return '';
    }
  }

  restorePolicyFromDocument(doc: RequiredDocumentSignature): void {
    if (doc.reuploadPolicy) {
      this.reuploadPolicy = doc.reuploadPolicy;
    }
    if (doc.reuploadDays) {
      this.reuploadDays = doc.reuploadDays;
    }
    if (doc.reuploadDate) {
      this.reuploadDate = parseISO(doc.reuploadDate);
    }
  }

  toggleDatePicker = (): void => {
    this.showDatePicker = !this.showDatePicker;
  };

  selectReuploadDate = (date: Date): void => {
    this.reuploadDate = date;
    this.showDatePicker = false;
  };

  zeroParentPadding = (element: HTMLElement): void => {
    const li = element.closest('.ember-power-select-option');
    if (li instanceof HTMLElement) {
      li.style.paddingTop = '0px';
      li.style.paddingBottom = '0px';
    }
  };

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @onClose={{@onClose}}
    >
      <FocusTrapper>
        <div class="relative" data-test-add-document-modal>

          {{! Header }}
          <div class="flex items-center justify-between px-6 py-4">
            {{! template-lint-disable attribute-indentation }}
            <h2 class="text-lg font-semibold m-0">{{if
                this.isEditing
                "Edit Required Document"
                "Add Required Document"
              }}</h2>
            <CloseButton
              class="!relative !top-auto !right-auto border-0 hover:bg-carbon-5 rounded"
              @onClick={{@onClose}}
            />
          </div>

          {{! Body }}
          <div class="px-6 pb-4 space-y-3">
            {{#if this.loadTemplates.isRunning}}
              <p class="text-sm text-carbon-50 text-center py-4">Loading document types…</p>
            {{else}}
              <div>
                <label id="add-doc-type-label" class="block text-sm font-medium text-carbon-50 leading-none mb-2">
                  Document type
                  <span class="inline-block w-1 h-1 rounded-full bg-red-40 ml-0.5 mb-1"></span>
                </label>
                <PowerSelect
                  aria-labelledby="add-doc-type-label"
                  @placeholder="Select a document type…"
                  @options={{this.dropdownOptions}}
                  @selected={{this.selectedOption}}
                  @onChange={{this.selectOption}}
                  @searchField="title"
                  @triggerClass="document-type-trigger"
                  @dropdownClass="in-modal"
                  data-test-document-type-select
                  as |option|
                >
                  {{#if (eq option.id "__separator__")}}
                    <hr class="border-carbon-20" style="margin: 0 -8px;" {{didInsert this.zeroParentPadding}} />
                    {{!-- TODO: Re-enable when inline template creation from dropdown is needed
                  {{else if (eq option.id "__create_new__")}}
                    <div class="flex items-center gap-2 text-carbon-50">
                      {{svgJar "circle-plus-carbon" width="16" height="16" class="shrink-0"}}
                      <span>Create new document type</span>
                    </div>
                  --}}
                  {{else if (eq option.id "__manage_types__")}}
                    <div class="flex items-center gap-2 text-carbon-50" data-test-manage-types-option>
                      {{svgJar "settings-cog-16" width="16" height="16" class="shrink-0"}}
                      <span
                        class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base leading-[180%] text-carbon-70"
                      >{{option.title}}</span>
                    </div>
                  {{else}}
                    <span
                      class="block flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base leading-[150%] text-carbon-70"
                    >{{option.title}}</span>
                  {{/if}}
                </PowerSelect>
              </div>

              {{#if this.selectedOption}}
                <div class="space-y-4 p-4 bg-carbon-5 border border-carbon-10 rounded-md" data-test-new-type-form>
                  <h4 class="text-base font-semibold text-carbon-70 m-0">Document details</h4>

                  {{! Name — read-only, editable only from manage modal }}
                  <div>
                    <label for="add-doc-title" class="block text-sm font-medium text-carbon-50 leading-none mb-1">
                      Name
                      <span class="inline-block w-1 h-1 rounded-full bg-red-40 ml-0.5 mb-1"></span>
                    </label>
                    <input
                      type="text"
                      id="add-doc-title"
                      class="w-full !rounded-md !border !border-carbon-20 !bg-carbon-5 px-2 py-2.5 text-base text-carbon-70"
                      value={{this.selectedOption.title}}
                      disabled
                      data-test-readonly-title
                    />
                  </div>

                  {{! Description — read-only, editable only from manage modal }}
                  <div>
                    <label for="add-doc-description" class="block text-sm font-medium text-carbon-50 leading-none mb-1">
                      Description
                    </label>
                    <textarea
                      id="add-doc-description"
                      class="w-full !rounded-md !border !border-carbon-20 !bg-carbon-5 px-2 py-2.5 text-base text-carbon-70 resize-none"
                      placeholder="Add instructions for the visitor"
                      rows="3"
                      disabled
                      data-test-readonly-description
                    >{{this.newDescription}}</textarea>
                  </div>

                  <div class="flex flex-col gap-1">
                    <div class="flex items-start space-x-3 pt-1">
                      <input
                        type="checkbox"
                        id="parsing-enabled"
                        class="custom-checkbox shrink-0"
                        checked={{this.parsingEnabled}}
                        {{on "change" (pick "target.checked" this.updateParsingEnabled)}}
                        data-test-parsing-enabled
                      />
                      <label for="parsing-enabled" class="text-sm text-carbon-70 cursor-pointer select-none">
                        Auto-extract expiration dates
                      </label>
                    </div>
                    <p class="text-xs text-carbon-40 m-0 ml-7" data-test-ai-disclaimer>
                      This feature uses artificial intelligence powered by Anthropic to identify expiration dates in
                      documents
                    </p>
                  </div>
                </div>
              {{/if}}

              <div class="p-4 border border-carbon-10 rounded-md" data-test-reupload-policy>
                <h3 class="text-base font-semibold text-carbon-70 m-0 mb-4">Expiration & re-upload rules</h3>
                <div class="flex flex-col gap-2">
                  <label class="flex items-center h-7 gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reupload-policy"
                      value="always"
                      checked={{eq this.reuploadPolicy "always"}}
                      {{on "change" this.updateReuploadPolicy}}
                      class="custom-radio shrink-0"
                      data-test-policy-always
                    />
                    <span class="text-sm font-medium text-carbon-50">Always require re-upload</span>
                  </label>

                  <label class="flex items-center h-7 gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reupload-policy"
                      value="never"
                      checked={{eq this.reuploadPolicy "never"}}
                      {{on "change" this.updateReuploadPolicy}}
                      class="custom-radio shrink-0"
                      data-test-policy-never
                    />
                    <span class="text-sm font-medium text-carbon-50">Never require re-upload</span>
                  </label>

                  <label class="flex items-center h-7 gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reupload-policy"
                      value="on_expiration"
                      checked={{eq this.reuploadPolicy "on_expiration"}}
                      {{on "change" this.updateReuploadPolicy}}
                      class="custom-radio shrink-0"
                      data-test-policy-on-expiration
                    />
                    <span class="text-sm font-medium text-carbon-50">Sync to document's expiration date</span>
                  </label>

                  <div class="flex items-center h-7 gap-2">
                    <label class="flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="after_days"
                        checked={{eq this.reuploadPolicy "after_days"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="custom-radio shrink-0"
                        data-test-policy-after-days
                      />
                      <span class="text-sm font-medium text-carbon-50">Set expiration duration</span>
                    </label>
                    {{#if (and this.selectedOption (eq this.reuploadPolicy "after_days"))}}
                      <input
                        type="number"
                        min="1"
                        max="9999"
                        step="1"
                        class="bg-white border border-carbon-10 text-base text-carbon-70 [appearance:textfield] [&amp;::-webkit-outer-spin-button]:appearance-none [&amp;::-webkit-inner-spin-button]:appearance-none"
                        style="width: 64px; height: 28px; padding: 0 8px; margin: 0; line-height: 2; border-radius: 6px; display: inline-block;"
                        value={{this.reuploadDays}}
                        {{on "input" (pick "target.value" this.updateReuploadDays)}}
                        data-test-policy-days-input
                      />
                      <span class="text-sm text-carbon-70">days</span>
                    {{/if}}
                  </div>

                  <div class="flex items-center h-7 gap-2">
                    <label class="flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="on_date_yearly"
                        checked={{eq this.reuploadPolicy "on_date_yearly"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="custom-radio shrink-0"
                        data-test-policy-on-date-yearly
                      />
                      <span class="text-sm font-medium text-carbon-50">Set a fixed expiration date</span>
                    </label>
                    {{#if (and this.selectedOption (eq this.reuploadPolicy "on_date_yearly"))}}
                      <div class="relative">
                        <button
                          type="button"
                          class="flex items-center gap-2 border rounded-md bg-white text-base cursor-pointer transition-colors duration-150 hover:border-carbon-40 whitespace-nowrap
                            {{if this.showDatePicker 'border-gem-50' 'border-carbon-10'}}"
                          style="padding: 2px 8px; line-height: 160%; border-radius: 6px;"
                          {{on "click" this.toggleDatePicker}}
                          data-test-policy-specific-date-trigger
                        >
                          <span
                            class="{{unless this.reuploadDate 'text-carbon-40'}} text-carbon-70"
                          >{{this.reuploadDateDisplay}}</span>
                          {{svgJar "calendar-16" width="16" height="16" class="shrink-0 text-carbon-70"}}
                        </button>
                        {{#if this.showDatePicker}}
                          {{! template-lint-disable use-ui-card }}
                          <div
                            class="p-3 bg-white border border-carbon-20 rounded-lg"
                            style="position: absolute; bottom: calc(100% + 8px); right: 0; box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10); z-index: 214748364;"
                            data-test-policy-specific-date-input
                          >
                            <EnvoyOneUpCalendar
                              @selectedDate={{this.reuploadDate}}
                              @onSelectDate={{this.selectReuploadDate}}
                            />
                          </div>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              </div>
            {{/if}}
          </div>

          {{! Footer }}
          <footer class="px-6 py-4">
            <ButtonGroup class="flex justify-end" @reverse={{true}}>
              <button
                type="button"
                class="btn btn--brand"
                disabled={{or (not this.canSave) this.saveDocument.isRunning}}
                {{on "click" (perform this.saveDocument)}}
                data-test-save
              >
                {{if this.saveDocument.isRunning "Saving…" (if this.isEditing "Save changes" "Add document")}}
              </button>
              <button
                type="button"
                class="btn btn--outlineFill"
                disabled={{this.saveDocument.isRunning}}
                {{on "click" @onClose}}
                data-test-cancel
              >
                Cancel
              </button>
            </ButtonGroup>
          </footer>

        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
